import { Form, FormInstance } from 'antd';
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFullAddress } from '@/hooks';
import { INormalizedAddress } from '@/types';

const ADDRESS_FIELDS = [
  'province',
  'city',
  'residenceLocality',
  'streetType',
  'streetNumber',
  'postalCode',
  'address'
] as const;

const NORMALIZED_ADDRESS_KEYS: (keyof INormalizedAddress)[] = [
  'address',
  'province',
  'city',
  'residenceLocality',
  'streetType',
  'streetNumber',
  'postalCode',
  'countryResidence'
];

const EXTRA_ADDRESS_KEYS = ['staircase', 'floor', 'door'] as const;

const ALL_ADDRESS_RELATED_KEYS = [...NORMALIZED_ADDRESS_KEYS, ...EXTRA_ADDRESS_KEYS];

type AddressField = (typeof ADDRESS_FIELDS)[number];

interface UseAddressFieldsParams {
  form: FormInstance;
  wrapperRef: RefObject<HTMLDivElement | null>;
}

export const useAddressFields = ({ form, wrapperRef }: UseAddressFieldsParams) => {
  const [autocompleteError, setAutocompleteError] = useState<string | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<INormalizedAddress | null>(null);
  const [initialAddressForAutocomplete, setInitialAddressForAutocomplete] = useState<
    string | undefined
  >(undefined);

  const fullAddress = useFullAddress(selectedAddress || undefined);

  const watchedUserInfo = Form.useWatch(['userInfo'], form);
  const hasHydratedRef = useRef(false);
  const hasUserSelectedAddressRef = useRef(false);

  useEffect(() => {
    if (hasUserSelectedAddressRef.current) return;
    if (!watchedUserInfo?.address) return;
    if (hasHydratedRef.current && selectedAddress?.address === watchedUserInfo.address) return;

    const hydratedAddress: INormalizedAddress = {};
    for (const key of NORMALIZED_ADDRESS_KEYS) {
      const value = watchedUserInfo[key];
      if (value !== undefined && value !== null) {
        (hydratedAddress as Record<string, string>)[key] = value;
      }
    }

    if (Object.keys(hydratedAddress).length > 0) {
      setSelectedAddress(hydratedAddress);

      const parts = [
        watchedUserInfo.streetType,
        watchedUserInfo.address,
        watchedUserInfo.city,
        watchedUserInfo.province
      ].filter(Boolean);
      setInitialAddressForAutocomplete(parts.join(', '));
    }
    hasHydratedRef.current = true;
  }, [watchedUserInfo, selectedAddress?.address]);

  const handleAddressSelect = useCallback(
    (address: INormalizedAddress) => {
      hasUserSelectedAddressRef.current = true;
      setSelectedAddress(address);

      for (const key of NORMALIZED_ADDRESS_KEYS) {
        const value = address[key];
        form.setFieldValue(
          ['userInfo', key],
          value !== undefined && value !== null && value !== '' ? value : null
        );
      }

      for (const key of EXTRA_ADDRESS_KEYS) {
        form.setFieldValue(['userInfo', key], null);
      }
    },
    [form]
  );

  const handleAddressClear = useCallback(() => {
    hasUserSelectedAddressRef.current = false;
    setSelectedAddress(null);

    for (const key of ALL_ADDRESS_RELATED_KEYS) {
      form.setFieldValue(['userInfo', key], null);
    }
  }, [form]);

  const missingFields = useMemo((): AddressField[] => {
    if (!selectedAddress?.address) return [];
    return ADDRESS_FIELDS.filter(field => !selectedAddress[field]);
  }, [selectedAddress]);

  const hasSelectedAddress = !!selectedAddress?.address;
  const hasMissingFields = missingFields.length > 0;
  const shouldShowAllFields = !!autocompleteError;

  const shouldShowField = useCallback(
    (field: AddressField): boolean => {
      if (shouldShowAllFields) return true;
      return hasSelectedAddress && missingFields.includes(field);
    },
    [hasSelectedAddress, missingFields, shouldShowAllFields]
  );

  useEffect(() => {
    if (hasMissingFields && hasSelectedAddress) {
      setTimeout(() => {
        if (wrapperRef.current) {
          let scrollableParent: HTMLElement | null = wrapperRef.current.parentElement;

          while (scrollableParent) {
            const style = window.getComputedStyle(scrollableParent);
            const hasOverflowY = style.overflowY === 'auto' || style.overflowY === 'scroll';
            const hasHeight = scrollableParent.scrollHeight > scrollableParent.clientHeight;

            if (hasOverflowY && hasHeight) {
              const elementTop = wrapperRef.current.offsetTop;
              const elementHeight = wrapperRef.current.offsetHeight;
              const parentHeight = scrollableParent.clientHeight;

              scrollableParent.scrollTo({
                top: elementTop + elementHeight - parentHeight + 20,
                behavior: 'smooth'
              });
              return;
            }

            scrollableParent = scrollableParent.parentElement;
          }

          const modalBody = document.querySelector('.ant-modal-body') as HTMLElement;
          if (modalBody && wrapperRef.current) {
            const elementTop = wrapperRef.current.getBoundingClientRect().top;
            const modalBodyTop = modalBody.getBoundingClientRect().top;
            const relativeTop = elementTop - modalBodyTop + modalBody.scrollTop;
            const elementHeight = wrapperRef.current.offsetHeight;
            const parentHeight = modalBody.clientHeight;

            modalBody.scrollTo({
              top: relativeTop + elementHeight - parentHeight + 20,
              behavior: 'smooth'
            });
          }
        }
      }, 150);
    }
  }, [hasMissingFields, hasSelectedAddress, selectedAddress?.address, wrapperRef]);

  return {
    selectedAddress,
    fullAddress,
    initialAddressForAutocomplete,
    autocompleteError,
    setAutocompleteError,
    handleAddressSelect,
    handleAddressClear,
    hasSelectedAddress,
    hasMissingFields,
    shouldShowAllFields,
    shouldShowField
  };
};
